/**
 * SAGA Execution Monitoring GraphQL Operations
 *
 * GraphQL queries and mutations for SAGA execution monitoring.
 * All operations follow FERP's data-driven architecture with complete
 * immutable audit trails and tenant isolation.
 *
 * Backend Integration: graphql_gateway/src/saga_api.rs
 * Issue: #256 - SAGA Execution Backend Integration
 */

import { gql } from '@apollo/client';

/**
 * Query single SAGA execution status
 * Returns complete execution state from repository (append-only DynamoDB)
 */
export const GET_SAGA_EXECUTION_STATUS = gql`
  query GetSagaExecutionStatus($executionId: ID!) {
    sagaExecutionStatus(executionId: $executionId) {
      executionId
      sagaId
      status
      startedAt
      currentState
      auditContext {
        correlationId
        sagaExecutionId
        tenantId
        initiator
        operationType
        timestamp
      }
    }
  }
`;

/**
 * Query multiple SAGA executions for tenant
 * Supports optional status filtering and configurable limits
 */
export const GET_SAGA_EXECUTIONS = gql`
  query GetSagaExecutions($limit: Int, $statusFilter: SagaExecutionStatus) {
    sagaExecutions(limit: $limit, statusFilter: $statusFilter) {
      executionId
      sagaId
      status
      startedAt
      completedAt
      currentState
      initiator
    }
  }
`;

/**
 * Transition SAGA state (life-critical operation)
 * Executes state transition through primitive registry with full audit trail
 */
export const TRANSITION_SAGA_STATE = gql`
  mutation TransitionSagaState($input: TransitionSagaStateInput!) {
    transitionSagaState(input: $input) {
      executionId
      sagaId
      status
      startedAt
      currentState
      auditContext {
        correlationId
        sagaExecutionId
        tenantId
        initiator
        operationType
        timestamp
      }
    }
  }
`;

/**
 * Compensate SAGA execution (LIFE-CRITICAL operation)
 * Triggers compensation flow with emergency escalation
 */
export const COMPENSATE_SAGA = gql`
  mutation CompensateSaga($input: CompensateSagaInput!) {
    compensateSaga(input: $input) {
      executionId
      sagaId
      status
      startedAt
      currentState
      auditContext {
        correlationId
        sagaExecutionId
        tenantId
        initiator
        operationType
        timestamp
      }
    }
  }
`;

/**
 * Start new SAGA execution
 * Creates new execution with backend Snowflake ID generation
 */
export const START_SAGA = gql`
  mutation StartSaga($input: StartSagaInput!) {
    startSaga(input: $input) {
      executionId
      sagaId
      status
      startedAt
      currentState
      auditContext {
        correlationId
        sagaExecutionId
        tenantId
        initiator
        operationType
        timestamp
      }
    }
  }
`;

/**
 * Get available SAGA definitions for tenant
 * Returns all configured SAGA workflows from repository
 */
export const GET_SAGA_DEFINITIONS = gql`
  query GetSagaDefinitions {
    sagaDefinitions {
      sagaId
      version
      description
      steps {
        stepId
        name
        kind
        actionHandler
        compensationHandler
        timeoutSeconds
        metadata
      }
      metadata
    }
  }
`;
